/**
 * ConfiguratorPage Component
 *
 * Full-page puppet and tool configuration interface.
 * Replaces the modal-based configurator for better performance.
 *
 * Features:
 * - Two-column layout (puppet list + tool configuration)
 * - Back button to return to main app
 * - Puppet management (create, edit, delete)
 * - Tool configuration (create, edit, delete, duplicate)
 * - Per-tool settings: min/max degrees, docking/starting positions, duration, easing
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { ConfirmDialog } from '../dialogs/ConfirmDialog';
import { usePuppetStore, type Puppet } from '@/stores/puppetStore';
import { PuppetList } from './PuppetList';
import { PuppetCreateForm } from './PuppetCreateForm';
import { PuppetEditForm } from './PuppetEditForm';
import { ToolConfig } from './ToolConfig';

/**
 * Compute initial selected puppet ID from store state
 */
function getInitialSelectedPuppetId(): string | null {
  const state = usePuppetStore.getState();
  if (state.activePuppetId) return state.activePuppetId;
  if (state.puppets.length > 0) return state.puppets[0].id;
  return null;
}

/**
 * Full-page configurator for managing puppets and their tools
 */
export function ConfiguratorPage() {
  const navigate = useNavigate();

  // Track selected puppet for editing
  const activePuppetId = usePuppetStore((state) => state.activePuppetId);
  const deletePuppet = usePuppetStore((state) => state.deletePuppet);
  const setActivePuppetById = usePuppetStore(
    (state) => state.setActivePuppetById
  );
  const puppets = usePuppetStore((state) => state.puppets);

  // Initialize with current active puppet or first puppet (computed once on mount)
  const [selectedPuppetId, setSelectedPuppetId] = useState<string | null>(
    getInitialSelectedPuppetId
  );
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [puppetToDelete, setPuppetToDelete] = useState<Puppet | null>(null);

  // Get selected puppet data - fall back to active puppet if selected was deleted
  const effectiveSelectedId = puppets.find((p) => p.id === selectedPuppetId)
    ? selectedPuppetId
    : activePuppetId || (puppets.length > 0 ? puppets[0].id : null);

  const selectedPuppet =
    puppets.find((p) => p.id === effectiveSelectedId) || null;

  const handleBack = () => {
    navigate('/');
  };

  const handleSelectPuppet = (puppetId: string) => {
    setSelectedPuppetId(puppetId);
    setShowCreateForm(false);
    setActivePuppetById(puppetId);
  };

  const handleAddPuppet = () => {
    setShowCreateForm(true);
  };

  const handlePuppetCreated = (puppetId: string) => {
    setShowCreateForm(false);
    setSelectedPuppetId(puppetId);
    setActivePuppetById(puppetId);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  const handleDeletePuppet = (puppet: Puppet) => {
    setPuppetToDelete(puppet);
  };

  const handleConfirmDelete = () => {
    if (puppetToDelete) {
      const success = deletePuppet(puppetToDelete.id);
      if (success) {
        if (selectedPuppetId === puppetToDelete.id) {
          const remaining = puppets.filter((p) => p.id !== puppetToDelete.id);
          if (remaining.length > 0) {
            setSelectedPuppetId(remaining[0].id);
            setActivePuppetById(remaining[0].id);
          }
        }
      }
      setPuppetToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setPuppetToDelete(null);
  };

  return (
    <>
      <div
        className="flex h-screen w-screen flex-col overflow-hidden"
        style={{ backgroundColor: 'var(--color-dark-bg)' }}
      >
        {/* Header */}
        <header
          className="flex items-center justify-between border-b px-6 py-4"
          style={{
            backgroundColor: 'var(--color-dark-panel)',
            borderColor: 'var(--color-dark-border)',
          }}
        >
          <div className="flex items-center gap-4">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors hover:bg-neutral-700"
              style={{ color: 'var(--color-dark-text)' }}
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Editor
            </button>
            <div
              className="h-6 w-px"
              style={{ backgroundColor: 'var(--color-dark-border)' }}
            />
            <h1
              className="text-lg font-semibold"
              style={{ color: 'var(--color-dark-text)' }}
            >
              Puppet Configurator
            </h1>
          </div>
        </header>

        {/* Main Content - Two Column Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Column - Puppet List (300px) */}
          <motion.div
            className="flex w-[300px] flex-shrink-0 flex-col border-r"
            style={{
              backgroundColor: 'var(--color-dark-panel)',
              borderColor: 'var(--color-dark-border)',
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="border-b px-4 py-3"
              style={{ borderColor: 'var(--color-dark-border)' }}
            >
              <h2
                className="text-sm font-medium"
                style={{ color: 'var(--color-dark-text)' }}
              >
                Puppets
              </h2>
            </div>

            {/* Create form or puppet list */}
            <div className="flex flex-1 flex-col overflow-hidden">
              <AnimatePresence mode="wait">
                {showCreateForm ? (
                  <motion.div
                    key="create-form"
                    className="p-3"
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                  >
                    <PuppetCreateForm
                      onCancel={handleCancelCreate}
                      onCreated={handlePuppetCreated}
                    />
                  </motion.div>
                ) : (
                  <motion.div
                    key="puppet-list"
                    className="flex-1 overflow-hidden"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <PuppetList
                      selectedPuppetId={selectedPuppetId}
                      onSelectPuppet={handleSelectPuppet}
                      onAddPuppet={handleAddPuppet}
                      onDeletePuppet={handleDeletePuppet}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* Right Column - Tool Configuration (flex-grow) */}
          <motion.div
            className="flex flex-1 flex-col overflow-hidden"
            style={{ backgroundColor: 'var(--color-dark-bg)' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.2, delay: 0.1 }}
          >
            {/* Tool Configuration Header */}
            <div
              className="border-b px-6 py-3"
              style={{
                backgroundColor: 'var(--color-dark-panel)',
                borderColor: 'var(--color-dark-border)',
              }}
            >
              <h2
                className="text-sm font-medium"
                style={{ color: 'var(--color-dark-text)' }}
              >
                {selectedPuppet ? 'Puppet Settings' : 'Tool Configuration'}
              </h2>
            </div>

            {/* Tool configuration content */}
            <AnimatePresence mode="wait">
              <motion.div
                key={effectiveSelectedId || 'no-selection'}
                className="flex flex-1 flex-col overflow-hidden"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.15 }}
              >
                {selectedPuppet && effectiveSelectedId ? (
                  <div className="flex flex-1 flex-col overflow-y-auto">
                    {/* Puppet Edit Form */}
                    <div
                      className="flex-shrink-0 border-b p-6"
                      style={{ borderColor: 'var(--color-dark-border)' }}
                    >
                      <PuppetEditForm puppet={selectedPuppet} />
                    </div>

                    {/* Tool Configuration */}
                    <div className="flex-1 overflow-hidden">
                      <ToolConfig puppetId={effectiveSelectedId} />
                    </div>
                  </div>
                ) : (
                  <div
                    className="flex h-full items-center justify-center text-sm"
                    style={{ color: 'var(--color-dark-text-muted)' }}
                  >
                    Select a puppet to configure its tools
                  </div>
                )}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!puppetToDelete}
        onOpenChange={(open) => !open && handleCancelDelete()}
        title="Delete Puppet"
        description={
          puppetToDelete
            ? `Are you sure you want to delete "${puppetToDelete.name}"? This will also delete all ${puppetToDelete.tools.length} tools associated with this puppet. This action cannot be undone.`
            : ''
        }
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        destructive
      />
    </>
  );
}
